// Отчёты руководителя над зеркалом exec-панели.
//
// ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ — САМОЕ ВАЖНОЕ В ФАЙЛЕ.
// Кассы, ФОТ и прибыли подразделения в этом разделе нет. В зеркале они лежат
// (`pex_fin_ops` — 4 462 выплаты людям на ≈103,6 млн ₽ и 257 ФИО на 28.07.2026, причём
// фамилии есть и в отдельной колонке, и внутри комментария), но решения о том, кто это видит,
// нет — поэтому ядро эти таблицы не читает вовсе (`pex_api.py`, список ЗАПРЕЩЕНО).
// Следствие написано НА ЭКРАНЕ, а не только в комментарии: формула прибыли в панели —
// «разница − ФОТ − расходы отдела − наличные», и без двух последних слагаемых вышла бы
// не прибыль, а разница минус счета поставщикам. Такое число хуже отсутствующего.
//
// ЧТО ЗДЕСЬ ЕСТЬ — ровно то, чего нет больше нигде:
//   • «Дебиторка по дням» — дневные снимки по юнитам. Пересчитать прошлую пятницу по
//     текущему Bitrix НЕЛЬЗЯ: стадии и даты оплаты с тех пор поменяли;
//   • «Переносы срока» — журнал сдвигов примерной даты оплаты. В Bitrix остаётся только
//     последнее значение, истории там нет;
//   • «Возраст ошибок» — когда косяк по сделке замечен впервые;
//   • «Руководители отделов» — эффективный руководитель, включая ручные заплатки панели.
//
// ДВА ИЗМЕРЕНИЯ НИГДЕ НЕ СКЛАДЫВАЮТСЯ. «Мои люди» (оргструктура) и «моя зона» (бизнес-юнит)
// связи между собой не имеют: `pex_dept_unit` пуст. Каждый блок подписан тем, по какому
// измерению он посчитан, и «Руководители» намеренно НЕ показывают дебиторку юнита рядом
// с людьми — это было бы приглашением сложить несложимое.
//
// ЭКРАН ТОЛЬКО ЧИТАЮЩИЙ. Ни одной записи ни в Bitrix, ни в панель, ни в зеркало.
// Разметка «косяк разобран» живёт в exec-панели и остаётся там.

import { useSearchParams } from 'react-router-dom'

import type { ПереносСтрока } from '../api/pex'
import {
  useДебиторкаПоДням, useДебиторкаПоЮнитам, useЖурналПлатежа, useКарточкуРуководителя,
  useКосяки, useОтделы, usePexRefs, useПереносы, useСводкаКосяков,
} from '../api/pex'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import {
  ПОРОГ_РОВНО, ЭШЕЛОН_ПЕРЕНОСОВ, возрастКосяка, видИзвестен, видПроверки, давность,
  дельтаКратко, источникРуководителя, классДельты, классПереносов, минутНазад, млн,
  направление, переносовСловами, поздниеПереносы, полнотаИстории, склонение, строкаЗапроса,
} from '../lib/pex'
import { fmtDate, fmtDays, fmtMoney, pluralN } from '../lib/format'
import { bitrixItemUrl } from '../lib/smart'

/** Блоки раздела. Ключ живёт в адресе (?r=), чтобы ссылку на отчёт можно было переслать. */
const БЛОКИ = [
  { key: 'recv', label: 'Дебиторка по дням', hint: 'Растёт долг или гаснет, по бизнес-юнитам' },
  { key: 'due', label: 'Переносы срока', hint: 'Кому и сколько раз сдвигали дату оплаты' },
  { key: 'kosyak', label: 'Возраст ошибок', hint: 'Сколько дней ошибка по сделке висит незакрытой' },
  { key: 'heads', label: 'Руководители отделов', hint: 'Кто эффективный руководитель и откуда это известно' },
] as const

type КлючБлока = (typeof БЛОКИ)[number]['key']

export default function Reports() {
  const [sp, setSp] = useSearchParams()
  const выбран = (БЛОКИ.find((b) => b.key === sp.get('r'))?.key ?? 'recv') as КлючБлока

  const выбрать = (k: КлючБлока) => {
    const next = new URLSearchParams(sp)
    next.set('r', k)
    // Параметры чужого блока чистим: иначе «страница 3» из переносов уезжала бы в косяки
    // и первый экран нового блока открывался бы с середины списка.
    for (const имя of ['start', 'chk', 'unit', 'min_moves', 'payment', 'head', 'no_head']) {
      next.delete(имя)
    }
    setSp(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <h1>Отчёты руководителя</h1>
        <div className="spacer" />
        <span className="hint">
          данные exec-панели, только чтение
        </span>
      </div>

      {/* Первое, что человек видит. Не «дисклеймер», а граница знания: без этой строки
          раздел будет прочитан как полный отчёт руководителя, каким он не является. */}
      <div className="notice" style={{ marginBottom: 16 }}>
        <b>Денег кассы и ФОТ в этом разделе нет.</b> Реестр наличных выплат и зарплат в ядро
        перенесён, но закрыт: решения о том, кому его показывать, пока нет. Поэтому здесь
        нельзя посчитать прибыль подразделения — в панели она считается как
        «разница − ФОТ − расходы отдела − наличные», и без двух последних слагаемых это была
        бы не прибыль, а разница минус счета поставщикам. Такое число опаснее отсутствующего.
        Всё, что ниже, — дебиторка, сроки и качество данных: ни зарплат, ни выплат людям.
      </div>

      <Свежесть />

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="filters">
          <div className="f" style={{ minWidth: 280 }}>
            <label htmlFor="rep-block">Отчёт</label>
            <select id="rep-block" value={выбран}
                    onChange={(e) => выбрать(e.target.value as КлючБлока)}>
              {БЛОКИ.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
          </div>
        </div>
        <div className="hint" style={{ marginTop: 10 }}>
          {БЛОКИ.find((b) => b.key === выбран)?.hint}
        </div>
      </div>

      {/* Блоки монтируются по одному: каждый делает свои запросы, и невыбранный не должен
          дёргать ядро. Это та же причина, по которой в «Кабинете» блок команды вынесен
          в отдельный компонент. */}
      {выбран === 'recv' && <БлокДебиторки />}
      {выбран === 'due' && <БлокПереносов />}
      {выбран === 'kosyak' && <БлокКосяков />}
      {выбран === 'heads' && <БлокРуководителей />}

      <ЧегоНет />
    </>
  )
}

/**
 * Свежесть. Два РАЗНЫХ числа рядом, и это главное:
 *   • когда ядро читало панель (журнал переносов раз в 15 мин, снимки раз в час);
 *   • какой ДЕНЬ лежит в данных — снимок дебиторки суточный, косяки не пополнялись с 17.07.
 * «Синк прошёл минуту назад» не означает «данные на сейчас», и пока эти два числа стоят
 * рядом, спутать их нельзя.
 */
function Свежесть() {
  const refs = usePexRefs()
  const f = refs.data?.fresh
  if (refs.isLoading) {
    return <div className="card" style={{ marginBottom: 16 }}>
      <div className="skeleton" style={{ width: '60%' }} />
    </div>
  }
  if (refs.error || !f) {
    return (
      <div className="notice err" style={{ marginBottom: 16 }}>
        Ядро не отдало справочники раздела, поэтому свежесть данных неизвестна. Числа ниже
        могут быть любой давности — это не «данных нет», это «мы не знаем, насколько они старые».
      </div>
    )
  }
  const сейчас = f.now
  const снимокДней = f.snapshot_day
    ? Math.max(0, Math.round((Date.parse(`${(сейчас ?? '').slice(0, 10)}T12:00:00Z`)
      - Date.parse(`${f.snapshot_day}T12:00:00Z`)) / 86_400_000))
    : null
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <dl className="dl">
        <dt>Ядро читало панель</dt>
        <dd>
          журнал переносов — {давность(минутНазад(f.due_log, сейчас))}; снимки дебиторки
          и косяки — {давность(минутНазад(f.recv, сейчас))}
          <span className="muted"> (синк ходит в панель раз в 15–60 минут)</span>
        </dd>
        <dt>В данных лежит</dt>
        <dd>
          снимок дебиторки за <b>{fmtDate(f.snapshot_day)}</b>
          {снимокДней !== null && снимокДней > 0 && (
            <span className="muted"> — это {снимокДней}{' '}
              {склонение(снимокДней, 'день', 'дня', 'дней')} назад</span>
          )}
          {'; '}последний перенос срока — {fmtDate(f.due_last_event, true)}
          {'; '}последний новый косяк — {fmtDate(f.kosyak_day)}
        </dd>
        <dt>Что это значит</dt>
        <dd className="muted">
          Снимок дебиторки <b>суточный</b>: свежий синк не делает его сегодняшним. Косяки
          панель дописывает, только когда её раздел открывают, — «новых косяков нет» здесь
          значит «в панель не заходили», а не «стало лучше». Историю снимков начали
          {' '}{fmtDate(f.snapshot_first_day)}: раньше этой даты сравнивать не с чем.
        </dd>
      </dl>
    </div>
  )
}

/**
 * Дебиторка по дням: растёт долг или гаснет.
 *
 * ЧЕТЫРЕ ЛОВУШКИ, КАЖДАЯ ПРОВЕРЕНА НА ЖИВЫХ ДАННЫХ И КАЖДАЯ НАПИСАНА НА ЭКРАНЕ.
 *
 * 1. «Юнит не заполнен» — крупнейшая строка: 236,1 млн ₽ из 426,7 млн, 55 % (на 28.07.2026).
 *    Она помечена и НЕ спрятана: спрячь — и сумма по юнитам окажется вдвое меньше долга
 *    компании, а объяснения этому на экране не будет.
 * 2. Синонимы юнитов. В снимках лежит сырое написание: «Архангельск (СиР)» и «Архангельск
 *    (не использовать)» — один Архангельск. Ядро склеивает их по справочнику панели, экран
 *    говорит, из скольких написаний собрана строка.
 * 3. Юнит есть не каждый день: «Спутниковый отдел» в 9 днях из 20. Пропуск дня — не нулевая
 *    дебиторка, а отсутствие строки, поэтому под каждым юнитом написано «данных N из M»,
 *    а дельта считается между крайними ИМЕЮЩИМИСЯ днями.
 * 4. Долг и просрочка ведут себя по-разному: за 20 дней долг вырос на 21,9 млн, а просрочка
 *    упала с 172,6 до 80,4 млн. Одна цифра «дебиторка» этого не показывает вовсе, поэтому
 *    их две и они рядом.
 */
function БлокДебиторки() {
  const [sp, setSp] = useSearchParams()
  const дней = ОКНА.includes(Number(sp.get('days'))) ? Number(sp.get('days')) : 30
  const дни = useДебиторкаПоДням(дней)
  const юниты = useДебиторкаПоЮнитам(дней)

  const строкиДней = дни.data?.rows ?? []
  const первый = строкиДней[0]
  const последний = строкиДней[строкиДней.length - 1]
  const дельтаДолга = первый && последний
    ? (последний.total ?? 0) - (первый.total ?? 0) : null
  const дельтаПросрочки = первый && последний
    ? (последний.overdue ?? 0) - (первый.overdue ?? 0) : null
  const строкиЮнитов = юниты.data?.rows ?? []
  const без = строкиЮнитов.find((r) => r.empty)

  const сменитьОкно = (v: string) => {
    const next = new URLSearchParams(sp)
    next.set('days', v)
    setSp(next, { replace: true })
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Дебиторка по дням</h2>
        <div className="spacer" />
        <span className="hint">
          считается по бизнес-юниту платежа, а не по отделу
        </span>
      </div>

      <FilterBar id="reports-window" style={{ marginBottom: 12 }}>
        <div className="f">
          <label htmlFor="rep-days">Окно</label>
          <select id="rep-days" value={дней} onChange={(e) => сменитьОкно(e.target.value)}>
            {ОКНА.map((d) => <option key={d} value={d}>{fmtDays(d)}</option>)}
          </select>
        </div>
      </FilterBar>

      <QueryState isLoading={дни.isLoading} error={дни.error}>
        {строкиДней.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Снимков за это окно нет</div>
            <div className="empty-hint">
              Историю снимков панель ведёт с 07.07.2026. Пустота здесь означает «дней в окне
              нет в данных», а не «дебиторка нулевая».
            </div>
          </div>
        ) : (
          <>
            <div className="tiles">
              <div className="tile">
                <div className="t-label">Долг на {fmtDate(последний?.snap_date)}</div>
                <div className="t-sum">{fmtMoney(последний?.total)}</div>
                <div className="t-label">{последний?.pays ?? '—'} неоплаченных платежей</div>
              </div>
              <div className={`tile${(последний?.overdue ?? 0) > 0 ? ' hot' : ''}`}>
                <div className="t-label">Из них просрочено</div>
                <div className="t-sum">{fmtMoney(последний?.overdue)}</div>
                <div className="t-label">
                  {последний?.total
                    ? `${Math.round(100 * (последний.overdue ?? 0) / последний.total)} % долга`
                    : '—'}
                </div>
              </div>
              <div className="tile">
                <div className="t-label">Долг за окно</div>
                <div className="t-sum">{дельтаКратко(дельтаДолга)}</div>
                <div className="t-label">
                  {направление(дельтаДолга) === 'вниз' ? 'гаснет'
                    : направление(дельтаДолга) === 'вверх' ? 'растёт' : 'на месте'}
                  {' '}с {fmtDate(первый?.snap_date)}
                </div>
              </div>
              <div className="tile">
                <div className="t-label">Просрочка за окно</div>
                <div className="t-sum">{дельтаКратко(дельтаПросрочки)}</div>
                <div className="t-label">
                  {направление(дельтаПросрочки) === 'вниз' ? 'гаснет' : 'растёт'}
                  {' '}— это не то же, что долг
                </div>
              </div>
              <div className="tile">
                <div className="t-label">Юнит не заполнен</div>
                <div className="t-sum">{fmtMoney(последний?.total_empty)}</div>
                <div className="t-label">
                  {последний?.total
                    ? `${Math.round(100 * (последний.total_empty ?? 0) / последний.total)} % долга`
                    : '—'}
                </div>
              </div>
            </div>

            {/* Главный вывод раздела словами, а не догадкой читателя. Ровно этот разрыв
                («долг стоит, а просрочка падает») и объясняется вторым блоком — переносами. */}
            {направление(дельтаДолга) !== 'вниз' && направление(дельтаПросрочки) === 'вниз' && (
              <div className="notice" style={{ marginTop: 12 }}>
                <b>Долг не гаснет, а просрочка падает.</b> За окно долг{' '}
                {направление(дельтаДолга) === 'ровно' ? 'не изменился' : 'вырос'} на{' '}
                {дельтаКратко(дельтаДолга)} ₽, а просрочка уменьшилась на{' '}
                {дельтаКратко(дельтаПросрочки)} ₽. Само по себе это не значит, что клиенты
                стали платить: просрочка исчезает и когда срок оплаты просто передвигают
                вперёд. Сколько раз и на сколько его передвигали — в отчёте «Переносы срока».
              </div>
            )}

            {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
            <div className="table-wrap cards" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th className="nowrap">Дата</th>
                    <th className="num">Долг</th>
                    <th className="num">Просрочено</th>
                    <th className="num">Платежей</th>
                    <th className="num">Юнитов</th>
                    <th className="num">Без юнита</th>
                  </tr>
                </thead>
                <tbody>
                  {[...строкиДней].reverse().map((r) => (
                    <tr key={r.snap_date}>
                      <td className="nowrap" data-label="Дата">{fmtDate(r.snap_date)}</td>
                      <td className="num" data-label="Долг">{млн(r.total)}</td>
                      <td className="num" data-label="Просрочено">{млн(r.overdue)}</td>
                      <td className="num" data-label="Платежей">{r.pays ?? '—'}</td>
                      <td className="num" data-label="Юнитов">{r.units ?? '—'}</td>
                      <td className="num" data-label="Без юнита">{млн(r.total_empty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="hint" style={{ marginTop: 10 }}>
              Суммы — миллионами. В выходные долг не меняется (в Bitrix никто не работает),
              а просрочка меняется: она считается от текущей даты, а не от снимка.
              {дни.data?.data_notes?.daily && ` ${дни.data.data_notes.daily}.`}
            </div>
          </>
        )}
      </QueryState>

      <h2 className="subhead">По бизнес-юнитам</h2>
      <QueryState isLoading={юниты.isLoading} error={юниты.error}>
        {строкиЮнитов.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Юнитов в снимках нет</div>
            <div className="empty-hint">Это отсутствие данных, а не нулевая дебиторка.</div>
          </div>
        ) : (
          <>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Бизнес-юнит</th>
                    <th className="num">Долг</th>
                    <th className="num">За окно</th>
                    <th className="num">Просрочено</th>
                    <th className="num">Просрочка за окно</th>
                    <th className="num">Платежей</th>
                    <th className="nowrap">Данных</th>
                  </tr>
                </thead>
                <tbody>
                  {строкиЮнитов.map((r) => (
                    <tr key={r.unit}>
                      <td data-label="Бизнес-юнит">
                        {r.empty
                          ? <span title="Так панель подписывает платежи без бизнес-юнита">
                              <b>юнит не заполнен</b>
                            </span>
                          : r.unit}
                        {r.merged_from > 1 && (
                          <div className="hint">
                            сведено из {r.merged_from} написаний по справочнику панели
                          </div>
                        )}
                      </td>
                      <td className="num" data-label="Долг">{млн(r.total)}</td>
                      <td className="num" data-label="За окно">
                        <span className={классДельты(r.delta)}>{дельтаКратко(r.delta)}</span>
                      </td>
                      <td className="num" data-label="Просрочено">
                        {r.overdue === null ? '—' : млн(r.overdue)}
                      </td>
                      <td className="num" data-label="Просрочка за окно">
                        <span className={классДельты(r.delta_overdue)}>
                          {дельтаКратко(r.delta_overdue)}
                        </span>
                      </td>
                      <td className="num" data-label="Платежей">{r.pays ?? '—'}</td>
                      <td className="nowrap" data-label="Данных">
                        <span className={r.days < r.days_window ? 'muted' : ''}>
                          {полнотаИстории(r.days, r.days_window)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="hint" style={{ marginTop: 10 }}>
              Прочерк вместо нуля стоит там, где сравнивать не с чем: у юнита с одним днём
              данных дельты не существует, и ноль читался бы как «долг не изменился».
              Порог «на месте» — {ПОРОГ_РОВНО.toLocaleString('ru-RU')} ₽: это наше соглашение,
              а не признак из данных.
              {без && ` Строка «юнит не заполнен» — ${млн(без.total)} ₽, и это не ошибка`
                + ' отчёта: у части платежей юнит в Bitrix действительно пуст.'}
            </div>
            <div className="hint">
              Дни без строки — это отсутствие строки, а не нулевой долг: панель пишет снимок
              только тем юнитам, у которых в этот день были неоплаченные платежи.
            </div>
          </>
        )}
      </QueryState>
    </div>
  )
}

/** Окна истории. 90 и 180 сейчас упрутся в 20 дней данных — и это видно по «данных N из M». */
const ОКНА = [7, 14, 30, 90, 180]

/**
 * Переносы срока оплаты — прямой ответ на «почему долг не гаснет».
 *
 * ЧЕГО В ЭТИХ ДАННЫХ НЕТ: КТО ПЕРЕНЁС. У журнала панели четыре колонки — платёж, старый
 * срок, новый срок, время, — и автора среди них не существует. Больше того, время — это
 * момент, когда синк ЗАМЕТИЛ изменение (все 478 событий на 28.07.2026 — на пяти часах суток),
 * а не когда человек нажал. Обе оговорки стоят на экране: без них первый же вопрос
 * руководителя будет «кто это сделал», и молчание прочитается как «система знает, но
 * не показывает».
 *
 * ПОЧЕМУ ЕДИНИЦА ОТЧЁТА — ПЛАТЁЖ. «Срок переносили пять раз» и «срок не наступил» — разные
 * объекты, и различает их число переносов. Порог «больше двух» взят из exec-панели: там он
 * отправлял платёж в «3-й эшелон» — то есть в список «это надо чинить, а не звонить».
 */
function БлокПереносов() {
  const [sp, setSp] = useSearchParams()
  const минимум = sp.get('min_moves') ?? ''
  const юнит = sp.get('unit') ?? ''
  const толькоНеоплаченные = sp.get('unpaid') === '1'
  const толькоПоздние = sp.get('late') === '1'
  const старт = Number(sp.get('start') ?? '0') || 0
  const выбранныйПлатёж = sp.get('payment') ?? ''

  const refs = usePexRefs()
  const путь = строкаЗапроса({
    min_moves: минимум, unit: юнит, late: толькоПоздние,
    paid: толькоНеоплаченные ? '0' : '', start: старт || '', sort: 'moves',
  })
  const данные = useПереносы(путь)
  const строки = данные.data?.rows ?? []

  const поставить = (имя: string, v: string) => {
    const next = new URLSearchParams(sp)
    if (v) next.set(имя, v)
    else next.delete(имя)
    if (имя !== 'start') next.delete('start')
    setSp(next, { replace: true })
  }

  // Итоги по загруженной странице, а не по всей выборке: ядро отдаёт агрегат только
  // счётчиком `total`, и любая сумма здесь — «по N из M». Так и подписано.
  const суммаСтраницы = строки.reduce((s, r) => s + (r.currency === 'RUB' ? (r.amount ?? 0) : 0), 0)
  const валютные = строки.filter((r) => r.currency !== 'RUB').length
  const поздних = строки.reduce((s, r) => s + (r.late_moves ?? 0), 0)
  const эшелон = строки.filter((r) => r.moves > ЭШЕЛОН_ПЕРЕНОСОВ).length

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Переносы срока оплаты</h2>
        <div className="spacer" />
        <span className="hint">
          журнал панели с 08.07.2026 — в Bitrix истории нет
        </span>
      </div>

      <FilterBar id="reports-due-moves" style={{ marginBottom: 12 }}>
        <div className="f">
          <label htmlFor="rep-moves">Переносов не меньше</label>
          <select id="rep-moves" value={минимум} onChange={(e) => поставить('min_moves', e.target.value)}>
            <option value="">любое число</option>
            <option value="2">двух</option>
            <option value="3">трёх — «третий эшелон»</option>
            <option value="4">четырёх</option>
          </select>
        </div>
        <div className="f" style={{ minWidth: 220 }}>
          <label htmlFor="rep-unit">Бизнес-юнит</label>
          <select id="rep-unit" value={юнит} onChange={(e) => поставить('unit', e.target.value)}>
            <option value="">любой</option>
            {(refs.data?.units ?? [])
              .filter((u) => u.unit !== refs.data?.empty_label)
              .map((u) => <option key={u.unit} value={u.unit}>{u.unit}</option>)}
          </select>
        </div>
        <div className="f">
          <label htmlFor="rep-unpaid">Оплата</label>
          <select id="rep-unpaid" value={толькоНеоплаченные ? '1' : ''}
                  onChange={(e) => поставить('unpaid', e.target.value)}>
            <option value="">все платежи</option>
            <option value="1">только неоплаченные</option>
          </select>
        </div>
        <div className="f">
          <label htmlFor="rep-late">Когда сдвигали</label>
          <select id="rep-late" value={толькоПоздние ? '1' : ''}
                  onChange={(e) => поставить('late', e.target.value)}>
            <option value="">когда угодно</option>
            <option value="1">после наступления срока</option>
          </select>
        </div>
      </FilterBar>

      {/* Фильтр по юниту здесь работает по НАЗВАНИЮ, и это не мелочь: у платежа юнит лежит
          строкой, а у сделки — числовым id того же по смыслу справочника. Список названий
          берём из снимков дебиторки, то есть из тех же данных, что и первый отчёт. */}
      <div className="hint" style={{ marginBottom: 12 }}>
        Юнит платежа в Bitrix — обычная строка с названием (у сделки тот же юнит хранится
        числовым кодом справочника). Поэтому отбор идёт по названию, и опечатка в нём даст
        пустой список, а не ошибку — список названий взят из снимков дебиторки.
      </div>

      <QueryState isLoading={данные.isLoading} error={данные.error}>
        {строки.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Под эти условия переносов нет</div>
            <div className="empty-hint">
              Журнал начат 08.07.2026 и содержит{' '}
              {refs.data
                ? pluralN(refs.data.counts.due_log, 'запись', 'записи', 'записей')
                : '— записей'}{' '}
              по{' '}
              {refs.data
                ? pluralN(refs.data.counts.due_payments, 'платежу', 'платежам', 'платежам')
                : '— платежам'}. Пустой список означает
              «под фильтр ничего не подошло», а не «сроки не переносят».
            </div>
          </div>
        ) : (
          <>
            <div className="tiles">
              <div className="tile">
                <div className="t-label">Платежей со сдвинутым сроком</div>
                <div className="t-sum">{данные.data?.total ?? '—'}</div>
                <div className="t-label">точный счётчик ядра</div>
              </div>
              <div className={`tile${эшелон ? ' hot' : ''}`}>
                <div className="t-label">Из них «третий эшелон»</div>
                <div className="t-sum">{эшелон}</div>
                <div className="t-label">
                  больше {ЭШЕЛОН_ПЕРЕНОСОВ} переносов — по {строки.length} из{' '}
                  {данные.data?.total ?? 0}
                </div>
              </div>
              <div className="tile">
                <div className="t-label">Сумма платежей</div>
                <div className="t-sum">{fmtMoney(суммаСтраницы)}</div>
                <div className="t-label">
                  по {строки.length} из {данные.data?.total ?? 0}
                  {валютные > 0 && `, ${валютные} в валюте не вошли`}
                </div>
              </div>
              <div className={`tile${поздних ? ' hot' : ''}`}>
                <div className="t-label">Сдвинуто после срока</div>
                <div className="t-sum">{поздних}</div>
                <div className="t-label">переносов на загруженной странице</div>
              </div>
            </div>

            <div className="table-wrap cards" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>Платёж</th>
                    <th className="nowrap">Юнит</th>
                    <th className="num">Сумма</th>
                    <th className="num">Переносов</th>
                    <th className="nowrap">Срок был → стал</th>
                    <th className="num">Сдвиг</th>
                    <th className="nowrap">Последний сдвиг</th>
                  </tr>
                </thead>
                <tbody>
                  {строки.map((r) => (
                    <СтрокаПереноса key={r.payment_bitrix_id} r={r}
                                    раскрыт={выбранныйПлатёж === String(r.payment_bitrix_id)}
                                    раскрыть={() => поставить('payment',
                                      выбранныйПлатёж === String(r.payment_bitrix_id)
                                        ? '' : String(r.payment_bitrix_id))} />
                  ))}
                </tbody>
              </table>
            </div>

            <Страницы данные={данные.data} старт={старт}
                      перейти={(s) => поставить('start', String(s))} />

            <div className="hint" style={{ marginTop: 10 }}>
              <b>Кто перенёс срок — в этих данных нет.</b>{' '}
              {данные.data?.data_notes?.no_author
                ?? 'у журнала панели четыре колонки, автора среди них не существует'}.
              Время переноса — момент, когда синк панели заметил изменение, а не когда человек
              нажал: поэтому все события стоят на часах прогона синка.
            </div>
            <div className="hint">
              «Сдвинуто после срока» — перенос, сделанный уже после того, как старый срок
              прошёл. Это не то же, что «уточнили дату заранее»: просрочка в отчёте по дням
              исчезает и в том, и в другом случае, а деньги приходят только в первом.
            </div>
          </>
        )}
      </QueryState>
    </div>
  )
}

/**
 * Строка платежа + раскрытый журнал его переносов. Журнал грузится только по раскрытию.
 *
 * `data-label` повторяют шапку таблицы из БлокПереносов: на телефоне шапки на экране нет,
 * и подпись поля карточки берётся из самой ячейки. Переименуют колонку в шапке —
 * переименовать надо и здесь, иначе на телефоне и на мониторе будут разные слова.
 */
function СтрокаПереноса({ r, раскрыт, раскрыть }: {
  r: ПереносСтрока
  раскрыт: boolean
  раскрыть: () => void
}) {
  return (
    <>
      <tr>
        <td data-label="Платёж">
          {/* Ссылка ведёт в Bitrix: своей карточки платежа в CRM пока нет, а раздел
              «Дебиторка» не принимает id платежа в адресе. */}
          <a href={bitrixItemUrl(134, r.payment_bitrix_id)} target="_blank" rel="noreferrer">
            {r.payment_title || `Платёж #${r.payment_bitrix_id}`}
          </a>
          <div className="hint">
            {[
              r.paid ? 'оплачен' : 'не оплачен',
              r.deal_bitrix_id ? `сделка #${r.deal_bitrix_id}` : 'сделка не привязана',
              !r.payment_in_core && 'платежа нет в ядре — удалён в Bitrix после записи',
            ].filter(Boolean).join(' · ')}
          </div>
        </td>
        <td className="nowrap" data-label="Юнит">
          {r.unit || <span className="muted">не заполнен</span>}
        </td>
        <td className="num" data-label="Сумма">
          {r.amount === null ? '—' : fmtMoney(r.amount, r.currency)}
        </td>
        <td className="num" data-label="Переносов">
          <button type="button" className={классПереносов(r.moves)} onClick={раскрыть}
                  title="Показать журнал переносов этого платежа"
                  style={{ border: 0, cursor: 'pointer' }}>
            {r.moves}
          </button>
        </td>
        <td className="nowrap" data-label="Срок был → стал">
          {fmtDate(r.due_first)} → {fmtDate(r.due_last)}
          {r.due_now && r.due_now !== r.due_last && (
            <div className="hint">в Bitrix сейчас {fmtDate(r.due_now)}</div>
          )}
        </td>
        <td className="num" data-label="Сдвиг">
          {r.days_shift === null ? '—' : `${r.days_shift > 0 ? '+' : ''}${r.days_shift} дн.`}
        </td>
        <td className="nowrap" data-label="Последний сдвиг">
          {fmtDate(r.moved_last)}
          <div className="hint">{поздниеПереносы(r.late_moves, r.moves)}</div>
        </td>
      </tr>
      {раскрыт && (
        <tr>
          <td colSpan={7}>
            <ЖурналПереносов id={r.payment_bitrix_id} />
          </td>
        </tr>
      )}
    </>
  )
}

/** Журнал одного платежа. Отдельный запрос и только по раскрытию строки. */
function ЖурналПереносов({ id }: { id: number }) {
  const q = useЖурналПлатежа(id)
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {(q.data?.rows ?? []).length === 0 ? (
        <div className="empty"><div className="empty-title">Событий по этому платежу в журнале нет.</div></div>
      ) : (
        <ul className="plain-list">
          {(q.data?.rows ?? []).map((e) => (
            <li key={e.evt_key}>
              {fmtDate(e.old_due)} → {fmtDate(e.new_due)}
              {' '}<span className="muted">
                ({e.days_added === null ? 'сдвиг неизвестен'
                  : `${e.days_added > 0 ? '+' : ''}${e.days_added} дн.`})
              </span>
              <div className="hint">
                замечено {fmtDate(e.changed_at, true)}
                {e.moved_after_due && ' — уже после того, как старый срок прошёл'}
              </div>
            </li>
          ))}
        </ul>
      )}
      {q.data?.payment === null && (
        <div className="hint">
          Самого платежа в ядре нет: его удалили в Bitrix после того, как панель записала
          переносы. Журнал сохранился, карточка — нет.
        </div>
      )}
    </QueryState>
  )
}

/** Пагинация. Отдельным компонентом: она нужна двум блокам, а разметка у них одна. */
function Страницы({ данные, старт, перейти }: {
  данные?: { total: number; next: number | null; limit: number; start: number }
  старт: number
  перейти: (s: number) => void
}) {
  if (!данные || данные.total <= данные.limit) return null
  const конец = Math.min(старт + данные.limit, данные.total)
  return (
    <div className="pager" style={{ marginTop: 10 }}>
      <button type="button" disabled={старт <= 0}
              onClick={() => перейти(Math.max(0, старт - данные.limit))}>
        ← назад
      </button>
      <span className="muted">
        {старт + 1}–{конец} из {данные.total}
      </span>
      <button type="button" disabled={данные.next === null}
              onClick={() => перейти(данные.next ?? старт)}>
        вперёд →
      </button>
    </div>
  )
}

/**
 * Возраст ошибок по сделкам.
 *
 * ГЛАВНОЕ: ВОЗРАСТ ОБРЕЗАН СЛЕВА, И ЭКРАН ОБЯЗАН ПИСАТЬ «≥».
 * Таблицу начали вести 07.07.2026, и 3 187 записей из 3 365 (95 %) помечены именно этой
 * датой: сколько ошибка висела до того, не знает никто. «Висит 19 дней» про такую запись —
 * ложь в самую удобную сторону: руководитель решит, что проблема свежая.
 *
 * ВТОРОЕ: разметка «разобрано» есть у ОДНОЙ записи из 3 365 (на 28.07.2026). Пустое поле
 * значит «никто не нажимал кнопку в панели», а не «не разобрано». Поэтому рядом стоит
 * другой признак — закрыта ли сделка в ядре сейчас (1 407 из 3 135 на 28.07.2026): он
 * не зависит от того, нажимал ли кто-то кнопку.
 */
function БлокКосяков() {
  const [sp, setSp] = useSearchParams()
  const вид = sp.get('chk') ?? ''
  const минДней = sp.get('min_days') ?? ''
  const скрытьЗакрытые = sp.get('open') === '1'
  const старт = Number(sp.get('start') ?? '0') || 0

  const свод = useСводкаКосяков()
  const путь = строкаЗапроса({
    chk: вид, min_days: минДней, closed: скрытьЗакрытые ? '0' : '',
    start: старт || '', sort: 'amount',
  })
  const данные = useКосяки(путь)
  const строки = данные.data?.rows ?? []
  const итог = свод.data?.summary

  const поставить = (имя: string, v: string) => {
    const next = new URLSearchParams(sp)
    if (v) next.set(имя, v)
    else next.delete(имя)
    if (имя !== 'start') next.delete('start')
    setSp(next, { replace: true })
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Возраст ошибок по сделкам</h2>
        <div className="spacer" />
        <span className="hint">
          считается по ответственному в сделке, а не по отделу
        </span>
      </div>

      <QueryState isLoading={свод.isLoading} error={свод.error}>
        {итог && (
          <>
            <div className="tiles">
              <div className="tile">
                <div className="t-label">Ошибок висит</div>
                <div className="t-sum">{итог.n}</div>
                <div className="t-label">на {итог.deals} сделках</div>
              </div>
              <div className="tile">
                <div className="t-label">Возраст известен точно</div>
                <div className="t-sum">{итог.n - итог.censored}</div>
                <div className="t-label">
                  у остальных {итог.censored} — «не меньше», а не «ровно»
                </div>
              </div>
              <div className="tile">
                <div className="t-label">Сделка уже закрыта</div>
                <div className="t-sum">{итог.deal_closed}</div>
                <div className="t-label">скорее всего неактуально</div>
              </div>
              <div className="tile">
                <div className="t-label">Отмечено «разобрано»</div>
                <div className="t-sum">{итог.reviewed}</div>
                <div className="t-label">кнопку в панели почти не нажимают</div>
              </div>
            </div>

            <div className="notice" style={{ marginTop: 12 }}>
              <b>Возраст этих ошибок обрезан слева.</b> Панель начала запоминать «замечено
              впервые» {fmtDate(свод.data?.since)}, и у{' '}
              {pluralN(итог.censored, 'записи', 'записей', 'записей')}
              {' '}из {итог.n} стоит именно эта дата. Значит их настоящий возраст —{' '}
              <b>не меньше</b> показанного, а насколько больше, не знает никто: до этой даты
              такого учёта не существовало. Числа с «≥» именно об этом.
            </div>

            <div className="table-wrap cards" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>Что не так</th>
                    <th className="num">Сделок</th>
                    <th className="nowrap">Возраст</th>
                    <th className="num">Уже закрыты</th>
                    <th className="num">Без юнита</th>
                    <th className="num">Сумма сделок</th>
                  </tr>
                </thead>
                <tbody>
                  {(свод.data?.rows ?? []).map((v) => {
                    const в = видПроверки(v.chk)
                    return (
                      <tr key={v.chk}>
                        <td data-label="Что не так">
                          <button type="button" className="badge work"
                                  onClick={() => поставить('chk', вид === v.chk ? '' : v.chk)}
                                  style={{ border: 0, cursor: 'pointer' }}
                                  title="Показать эти сделки в списке ниже">
                            {в.ярлык}
                          </button>
                          <div className="hint">
                            {в.смысл}
                            {!видИзвестен(v.chk)
                              && ' (код проверки нам незнаком — показан как есть)'}
                          </div>
                        </td>
                        <td className="num" data-label="Сделок">{v.n}</td>
                        <td className="nowrap" data-label="Возраст">
                          {возрастКосяка(v.days_median, v.censored > v.n / 2)}
                          <div className="hint">
                            от {возрастКосяка(v.days_min, false)} до{' '}
                            {возрастКосяка(v.days_max, v.censored > 0)}
                          </div>
                        </td>
                        <td className="num" data-label="Уже закрыты">{v.deal_closed}</td>
                        <td className="num" data-label="Без юнита">{v.no_unit}</td>
                        <td className="num" data-label="Сумма сделок">{млн(v.amount_rub)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="hint" style={{ marginTop: 10 }}>
              «Сумма сделок» — сумма сделок, на которых висит ошибка, и только рублёвых:
              курса в ядре нет. Это <b>не размер ущерба</b>: у большинства проверок ошибка —
              в оформлении, а не в деньгах. Возраст в столбце — медиана по виду: среднее
              здесь бесполезно, потому что 95 % записей стоят на одной дате.
            </div>
          </>
        )}
      </QueryState>

      <h2 className="subhead">Сделки</h2>

      <FilterBar id="reports-kosyak-deals" style={{ marginBottom: 12 }}>
        <div className="f" style={{ minWidth: 240 }}>
          <label htmlFor="rep-chk">Вид проверки</label>
          <select id="rep-chk" value={вид} onChange={(e) => поставить('chk', e.target.value)}>
            <option value="">любой</option>
            {/* Список видов берём из той же сводки, что нарисована выше: второй источник
                (справочник раздела) однажды разошёлся бы с ней, и в фильтре появился бы
                вид, которого в таблице нет. */}
            {(свод.data?.rows ?? []).map((v) => (
              <option key={v.chk} value={v.chk}>{видПроверки(v.chk).ярлык} ({v.n})</option>
            ))}
          </select>
        </div>
        <div className="f">
          <label htmlFor="rep-age">Висит не меньше</label>
          <select id="rep-age" value={минДней} onChange={(e) => поставить('min_days', e.target.value)}>
            <option value="">любого срока</option>
            <option value="7">недели</option>
            <option value="14">двух недель</option>
            <option value="30">месяца</option>
          </select>
        </div>
        <div className="f">
          <label htmlFor="rep-open">Состояние сделки</label>
          <select id="rep-open" value={скрытьЗакрытые ? '1' : ''}
                  onChange={(e) => поставить('open', e.target.value)}>
            <option value="">любое</option>
            <option value="1">только открытые</option>
          </select>
        </div>
      </FilterBar>

      <QueryState isLoading={данные.isLoading} error={данные.error}>
        {строки.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Под эти условия сделок нет</div>
            <div className="empty-hint">
              Это «под фильтр ничего не подошло», а не «ошибок нет».
            </div>
          </div>
        ) : (
          <>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Сделка</th>
                    <th className="nowrap">Что не так</th>
                    <th className="nowrap">Висит</th>
                    <th className="nowrap">Юнит</th>
                    <th className="num">Сумма</th>
                    <th className="nowrap">Состояние</th>
                  </tr>
                </thead>
                <tbody>
                  {строки.map((r) => (
                    <tr key={r.seen_key}>
                      <td data-label="Сделка">
                        <EntityLink kind="deal" id={r.deal_bitrix_id}
                                    title={r.deal_title || `#${r.deal_bitrix_id}`} />
                        {!r.deal_in_core && (
                          <div className="hint">сделки нет в ядре — удалена в Bitrix</div>
                        )}
                      </td>
                      <td className="nowrap" data-label="Что не так">
                        <span className="badge work">{видПроверки(r.chk).ярлык}</span>
                      </td>
                      <td className="nowrap" data-label="Висит">
                        {возрастКосяка(r.days, r.age_censored)}
                        <div className="hint">
                          замечено {fmtDate(r.first_seen)}
                          {r.age_censored && ' — в первый день учёта'}
                        </div>
                      </td>
                      <td className="nowrap" data-label="Юнит">
                        {r.unit || <span className="muted">не заполнен</span>}
                      </td>
                      <td className="num" data-label="Сумма">
                        {r.opportunity === null
                          ? '—' : fmtMoney(r.opportunity, r.currency || 'RUB')}
                      </td>
                      <td className="nowrap" data-label="Состояние">
                        {r.deal_closed
                          ? <span className="badge won">сделка закрыта</span>
                          : <span className="badge lose">открыта</span>}
                        {r.reviewed && (
                          <div className="hint">
                            разобрал {r.reviewed_by || 'кто-то'} {fmtDate(r.reviewed_at)}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Страницы данные={данные.data} старт={старт}
                      перейти={(s) => поставить('start', String(s))} />

            <div className="hint" style={{ marginTop: 10 }}>
              «Сделка закрыта» — состояние в ядре <b>сейчас</b>. Это честнее разметки
              «разобрано»: её ставят вручную в exec-панели, и почти никто её не ставит
              {итог && ` — на ${pluralN(итог.n, 'запись', 'записи', 'записей')} она стоит`}
              {итог && ` у ${итог.reviewed}`}. Пустое поле «разобрал» значит «никто
              не нажимал кнопку», а не «не разобрано».
            </div>
            <div className="hint">
              Разметку «разобрано» этот раздел не ставит и ставить не будет: он только читает.
              Отметить косяк разобранным по-прежнему можно в exec-панели.
            </div>
          </>
        )}
      </QueryState>
    </div>
  )
}

/**
 * Руководители отделов: кто эффективный руководитель и ОТКУДА это известно.
 *
 * Формула одна на всю CRM: эффективный руководитель = ручная заплатка панели, а если её нет,
 * то поле «Руководитель» у отдела в Bitrix. Разница между «из Bitrix» и «заплатка» важна:
 * заплатка в Bitrix не пишется, то есть в ядре ей взяться неоткуда — она существует только
 * потому, что зеркало панели перенесено.
 *
 * ЭТОТ БЛОК СЧИТАЕТ ТОЛЬКО «ПО МОИМ ЛЮДЯМ» — и говорит это. Дебиторки юнита здесь
 * намеренно НЕТ рядом с числом людей: связи «отдел ↔ бизнес-юнит» не существует
 * (`pex_dept_unit` пуст), и поставить эти два числа в одну строку значило бы пригласить их
 * сложить. Живая проверка: у руководителя сервиса СПб 26 человек и ноль неоплаченных
 * платежей на них, при дебиторке юнита «Санкт-Петербург» 44,5 млн ₽.
 */
function БлокРуководителей() {
  const [sp, setSp] = useSearchParams()
  const безРуководителя = sp.get('no_head') === '1'
  const выбран = sp.get('head') ?? ''
  const старт = Number(sp.get('start') ?? '0') || 0

  const путь = строкаЗапроса({
    no_head: безРуководителя ? '1' : '', start: старт || '', limit: 200,
  })
  const данные = useОтделы(путь)
  const строки = данные.data?.rows ?? []
  const refs = usePexRefs()
  const счёт = refs.data?.counts

  /**
   * Сколько отделов вообще без руководителя. Считаем по загруженным строкам, а не
   * вычитанием «отделы − с UF_HEAD − заплатки»: заплатку могли поставить отделу, у которого
   * UF_HEAD и так заполнен, и вычитание соврало бы на единицу. Все 101 строка (28.07.2026)
   * приходят одной страницей (limit=200), а при включённом фильтре это и есть `total`.
   */
  const безРук = безРуководителя
    ? (данные.data?.total ?? 0)
    : строки.filter((r) => !r.head_effective).length

  const поставить = (имя: string, v: string) => {
    const next = new URLSearchParams(sp)
    if (v) next.set(имя, v)
    else next.delete(имя)
    if (имя !== 'start') next.delete('start')
    setSp(next, { replace: true })
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Руководители отделов</h2>
        <div className="spacer" />
        <span className="hint">
          считается по оргструктуре Bitrix, а не по деньгам
        </span>
      </div>

      {счёт && (
        <div className="tiles" style={{ marginBottom: 12 }}>
          <div className="tile">
            <div className="t-label">Отделов</div>
            <div className="t-sum">{счёт.depts}</div>
            <div className="t-label">в оргструктуре</div>
          </div>
          <div className="tile">
            <div className="t-label">Руководитель в Bitrix</div>
            <div className="t-sum">{счёт.depts_with_head}</div>
            <div className="t-label">поле UF_HEAD заполнено</div>
          </div>
          <div className="tile">
            <div className="t-label">Заплаток панели</div>
            <div className="t-sum">{счёт.head_override}</div>
            <div className="t-label">вписано руками, в Bitrix не уходит</div>
          </div>
          <div className={`tile${безРук > 0 ? ' hot' : ''}`}>
            <div className="t-label">Руководителя нет вовсе</div>
            <div className="t-sum">{безРук}</div>
            <div className="t-label">для них отчёт «по людям» пуст у всех</div>
          </div>
        </div>
      )}

      <div className="notice" style={{ marginBottom: 12 }}>
        Руководителем считается <b>поле «Руководитель» у отдела</b>, а не должность
        в карточке сотрудника: для Bitrix «технический руководитель сервисного отдела» —
        просто текст. Если поле пусто, отчёт «по моим людям» пуст у <b>любого</b> сотрудника
        этого отдела, и это не про конкретного человека. Чинится в Bitrix заполнением поля,
        а не здесь.
      </div>

      <FilterBar id="reports-heads" style={{ marginBottom: 12 }}>
        <div className="f">
          <label htmlFor="rep-nohead">Показать</label>
          <select id="rep-nohead" value={безРуководителя ? '1' : ''}
                  onChange={(e) => поставить('no_head', e.target.value)}>
            <option value="">все отделы</option>
            <option value="1">только без руководителя</option>
          </select>
        </div>
      </FilterBar>

      <QueryState isLoading={данные.isLoading} error={данные.error}>
        {строки.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Отделов под это условие нет</div>
            <div className="empty-hint">
              {безРуководителя
                ? 'Руководитель есть у всех отделов — либо в Bitrix, либо заплаткой панели.'
                : 'Оргструктуры в ответе ядра нет. Это отсутствие данных, а не отсутствие отделов.'}
            </div>
          </div>
        ) : (
          <>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Отдел</th>
                    <th className="nowrap">Эффективный руководитель</th>
                    <th className="nowrap">Откуда известно</th>
                    <th className="num">Людей</th>
                  </tr>
                </thead>
                <tbody>
                  {строки.map((r) => {
                    const и = источникРуководителя(r.head_source)
                    return (
                      <tr key={r.dept_id}>
                        <td data-label="Отдел">
                          {r.dept_name || `Отдел #${r.dept_id}`}
                          {' '}<span className="muted mono">#{r.dept_id}</span>
                        </td>
                        <td className="nowrap" data-label="Эффективный руководитель">
                          {r.head_effective
                            ? (
                              <button type="button" className="badge work"
                                      style={{ border: 0, cursor: 'pointer' }}
                                      onClick={() => поставить('head',
                                        выбран === String(r.head_effective)
                                          ? '' : String(r.head_effective))}
                                      title="Показать, что висит на его людях">
                                {r.head_effective_name || `#${r.head_effective}`}
                              </button>
                            )
                            : <span className="muted">не указан</span>}
                        </td>
                        <td className="nowrap" data-label="Откуда известно">
                          <span className={r.head_source === 'override' ? 'badge work'
                            : r.head_source === 'bitrix' ? 'badge won' : 'badge lose'}>
                            {и.ярлык}
                          </span>
                          {r.head_source === 'override' && (
                            <div className="hint">
                              вписал {r.override_set_by || 'кто-то'}{' '}
                              {fmtDate(r.override_set_at)}
                            </div>
                          )}
                        </td>
                        <td className="num" data-label="Людей">{r.people}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="hint" style={{ marginTop: 10 }}>
              «Людей» — только действующие сотрудники: уволенные остаются ответственными
              в старых сделках, но в составе отдела их нет. Нажатие на фамилию показывает,
              что висит на его людях — по оргструктуре, без денег юнита.
            </div>
          </>
        )}
      </QueryState>

      {выбран && <КарточкаРуководителя bx={выбран} />}
    </div>
  )
}

/**
 * Что висит на людях руководителя. Единственное место раздела, где два измерения
 * упомянуты рядом — и рядом же сказано, почему их нельзя сложить.
 */
function КарточкаРуководителя({ bx }: { bx: string }) {
  const q = useКарточкуРуководителя(bx)
  const s = q.data?.summary
  return (
    <div style={{ marginTop: 16 }}>
      <h2 className="subhead">
        По его людям {q.data && <span className="muted mono">#{bx}</span>}
      </h2>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {!s ? (
          <div className="muted">Данных по этому руководителю ядро не отдало.</div>
        ) : (
          <>
            <div className="tiles">
              <div className="tile">
                <div className="t-label">Отделов в зоне</div>
                <div className="t-sum">{s.depts}</div>
                <div className="t-label">
                  {s.roots === s.depts ? 'без подчинённых'
                    : `свои: ${s.roots}, подчинённые: ${s.depts - s.roots}`}
                </div>
              </div>
              <div className="tile">
                <div className="t-label">Людей</div>
                <div className="t-sum">{s.people}</div>
                <div className="t-label">действующих, включая его самого</div>
              </div>
              <div className={`tile${s.kosyak ? ' hot' : ''}`}>
                <div className="t-label">Ошибок на их сделках</div>
                <div className="t-sum">{s.kosyak}</div>
                <div className="t-label">на {s.kosyak_deals} сделках</div>
              </div>
              <div className={`tile${s.due_payments ? ' hot' : ''}`}>
                <div className="t-label">Платежей со сдвинутым сроком</div>
                <div className="t-sum">{s.due_payments}</div>
                <div className="t-label">ответственный — его человек</div>
              </div>
            </div>

            {/* Ровно та ловушка, ради которой раздел и написан. Ноль здесь настоящий. */}
            <div className="notice" style={{ marginTop: 12 }}>
              <b>Это разрез «по моим людям», и только он.</b> Ответственный сделки или платежа
              входит в поддерево его отделов. Дебиторки бизнес-юнита рядом нет намеренно:
              связи «отдел ↔ бизнес-юнит» не существует ни в Bitrix, ни в панели, и сложить
              эти числа нельзя. Живой случай (замер 28.07.2026): у руководителя сервиса СПб 26 действующих
            сотрудников в поддереве из пяти отделов и ноль
              платежей со сдвинутым сроком, а по юниту «Санкт-Петербург» долг 44,5 млн ₽ —
              потому что деньги по Петербургу висят на координаторах чужого отдела.
              {/* Условие через «или», а не «и», намеренно: на живых данных у того же Орлова
                  три ошибки и НОЛЬ переносов срока. Требуй мы двух нулей сразу — пояснение
                  не показалось бы именно в том случае, ради которого написано. */}
              {(s.kosyak === 0 || s.due_payments === 0) && ' Ноль на плитках выше — из этой'
                + ' же серии: он означает «на его людях этого не висит», а не «данных нет».'}
            </div>

            {(q.data?.kosyak_by_check ?? []).length > 0 && (
              <div className="table-wrap cards" style={{ marginTop: 12 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Что не так</th>
                      <th className="num">Сделок</th>
                      <th className="nowrap">Висит</th>
                      <th className="num">Уже закрыты</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(q.data?.kosyak_by_check ?? []).map((v) => (
                      <tr key={v.chk}>
                        <td data-label="Что не так">
                          {видПроверки(v.chk).ярлык}
                          <div className="hint">{видПроверки(v.chk).смысл}</div>
                        </td>
                        <td className="num" data-label="Сделок">{v.n}</td>
                        <td className="nowrap" data-label="Висит">
                          {возрастКосяка(v.days_min, true)} – {возрастКосяка(v.days_max, true)}
                        </td>
                        <td className="num" data-label="Уже закрыты">{v.deal_closed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(q.data?.due_payments ?? []).length > 0 && (
              <>
                <h2 className="subhead">
                  Платежи его людей, которым сдвигали срок
                </h2>
                <ul className="plain-list">
                  {(q.data?.due_payments ?? []).map((p) => (
                    <li key={p.payment_bitrix_id}>
                      <a href={bitrixItemUrl(134, p.payment_bitrix_id)} target="_blank"
                         rel="noreferrer">
                        {p.payment_title || `Платёж #${p.payment_bitrix_id}`}
                      </a>
                      <div className="hint">
                        {[
                          `перенесли ${переносовСловами(p.moves)}`,
                          p.due_last ? `текущий срок ${fmtDate(p.due_last)}` : 'срока нет',
                          p.amount === null ? '' : fmtMoney(p.amount, p.currency),
                          p.unit || 'юнит не заполнен',
                          p.paid ? 'оплачен' : 'не оплачен',
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </li>
                  ))}
                </ul>
                {/* Сколько строк отдаст ядро, фронт не задаёт: параметра у /pex/head нет.
                    Поэтому оба числа берутся из ответа — показанное и общее (`summary`), —
                    а сама оговорка появляется, только если список и правда обрезан.
                    Раньше здесь стояли слова «первые 20»: у руководителя с пятью такими
                    платежами это было прямой неправдой. */}
                {(q.data?.due_payments ?? []).length < (q.data?.summary.due_payments ?? 0) && (
                  <div className="hint" style={{ marginTop: 8 }}>
                    Показаны первые {(q.data?.due_payments ?? []).length}
                    {' '}из {q.data?.summary.due_payments} по числу переносов.
                    Полный список — в отчёте «Переносы срока».
                  </div>
                )}
              </>
            )}
          </>
        )}
      </QueryState>
    </div>
  )
}

/**
 * Границы знания — отдельной карточкой в конце. Не «оговорки для галочки»: каждый пункт
 * закрывает конкретный способ прочитать раздел неправильно.
 */
function ЧегоНет() {
  return (
    <div className="card">
      <h2>Чего в этом разделе нет и почему</h2>
      <ul className="plain-list">
        <li className="muted">
          <b>Кассы, ФОТ и прибыли подразделения.</b> Реестр наличных выплат перенесён в ядро,
          но закрыт: там выплаты людям с фамилиями, и решения о доступе пока нет. Ядро эти
          таблицы не читает вовсе — не «фильтрует по правам», а не читает. Поэтому прибыль
          подразделения здесь не считается: без ФОТ и наличных получилась бы разница минус
          счета поставщикам, названная прибылью. Откроют доступ — появится и отчёт.
        </li>
        <li className="muted">
          <b>Единой цифры «моя зона».</b> Отдел и бизнес-юнит в Bitrix не связаны, а таблица
          соответствия в панели пуста. Поэтому «Руководители отделов» считают только людей,
          а «Дебиторка по дням» — только юниты, и рядом они не стоят.
        </li>
        <li className="muted">
          <b>Автора переноса срока.</b> В журнале панели четыре колонки, автора среди них нет.
          Время переноса — момент, когда синк заметил изменение, а не когда человек нажал.
        </li>
        <li className="muted">
          <b>Настоящего возраста ошибок старше 07.07.2026.</b> До этой даты «замечено впервые»
          никто не записывал, поэтому у 95 % записей возраст — «не меньше показанного».
        </li>
        <li className="muted">
          <b>Отчётного месяца.</b> В панели «за месяц» — это отчётный период, пара текстовых
          полей по-русски, и с бухгалтерским месяцем он не совпадает. В этом разделе таких
          периодов нет вовсе: только календарные даты снимков и событий. Поэтому с отчётами
          бухгалтерии числа отсюда совпадать не обязаны — они и не про то.
        </li>
        <li className="muted">
          <b>Нормы и плана.</b> В exec-панели у каждого числа была средняя за три месяца.
          Здесь её нет: истории снимков всего три недели, и «средняя за три месяца» по ним
          была бы выдумкой.
        </li>
        <li className="muted">
          <b>Правок.</b> Раздел только читает. Разметка «косяк разобран» и ручное назначение
          руководителя отделу остаются в exec-панели: они меняют то, что видят другие люди,
          и переносить их надо отдельно и осознанно.
        </li>
      </ul>
    </div>
  )
}
